import { useState, useEffect } from 'react';
import { Users, UserPlus, Trash2, ChevronDown, Shield, Eye, Edit3, Crown, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_CONFIG = {
  owner: { label: 'Owner', icon: Crown, color: 'text-amber-700 dark:text-amber-400', bg: 'bg-amber-100 dark:bg-amber-950/20 border-amber-300 dark:border-amber-900/40' },
  editor: { label: 'Editor', icon: Edit3, color: 'text-cyan-700 dark:text-cyan-400', bg: 'bg-cyan-100 dark:bg-cyan-950/20 border-cyan-300 dark:border-cyan-900/40' },
  viewer: { label: 'Viewer', icon: Eye, color: 'text-muted-foreground', bg: 'bg-muted/50 border-border' },
};

export function RoleBadge({ role }) {
  const cfg = ROLE_CONFIG[role] || ROLE_CONFIG.viewer;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-heading uppercase tracking-wider rounded-sm border ${cfg.bg} ${cfg.color}`}
      data-testid="role-badge">
      <Icon className="w-3 h-3" /> {cfg.label}
    </span>
  );
}

export default function TeamPanel({ projectId, userRole }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('viewer');
  const [inviting, setInviting] = useState(false);
  const [error, setError] = useState('');
  const [showInvite, setShowInvite] = useState(false);

  const token = localStorage.getItem('fmiq_token');
  const isOwner = userRole === 'owner';

  const fetchMembers = () => {
    fetch(`${API_URL}/api/closeout/projects/${projectId}/members`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => { setMembers(d.members || []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { fetchMembers(); }, [projectId]);

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/closeout/projects/${projectId}/members`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: inviteEmail.trim(), role: inviteRole }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || 'Failed to invite');
      } else {
        setInviteEmail('');
        setShowInvite(false);
        fetchMembers();
      }
    } catch {
      setError('Network error');
    }
    setInviting(false);
  };

  const handleUpdateRole = async (memberId, newRole) => {
    await fetch(`${API_URL}/api/closeout/projects/${projectId}/members/${memberId}`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ role: newRole }),
    });
    fetchMembers();
  };

  const handleRemove = async (memberId) => {
    if (!window.confirm('Remove this member from the project?')) return;
    await fetch(`${API_URL}/api/closeout/projects/${projectId}/members/${memberId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    fetchMembers();
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground py-12 justify-center">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading team...
      </div>
    );
  }

  return (
    <div data-testid="team-panel" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Users className="w-5 h-5 text-primary" />
          <h2 className="font-heading text-sm uppercase tracking-widest text-foreground/80">
            Project Team ({members.length})
          </h2>
        </div>
        {isOwner && (
          <button
            onClick={() => setShowInvite(!showInvite)}
            data-testid="invite-member-btn"
            className="flex items-center gap-2 px-3 py-2 text-xs font-heading uppercase tracking-wider text-primary border border-primary/30/50 rounded-sm hover:bg-primary/10 transition-colors"
          >
            <UserPlus className="w-3.5 h-3.5" /> Invite Member
          </button>
        )}
      </div>

      {/* Invite Form */}
      {showInvite && isOwner && (
        <div className="bg-card border border-border rounded-sm p-4 space-y-3" data-testid="invite-form">
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <label className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground mb-1 block">Email</label>
              <input
                type="email"
                value={inviteEmail}
                onChange={e => setInviteEmail(e.target.value)}
                placeholder="colleague@company.com"
                data-testid="invite-email-input"
                className="w-full bg-muted border border-border rounded-sm px-3 py-2 text-sm text-foreground/80 focus:outline-none focus:border-orange-800"
              />
            </div>
            <div>
              <label className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground mb-1 block">Role</label>
              <select
                value={inviteRole}
                onChange={e => setInviteRole(e.target.value)}
                data-testid="invite-role-select"
                className="bg-muted border border-border rounded-sm px-3 py-2 text-sm text-foreground/80 focus:outline-none focus:border-orange-800"
              >
                <option value="viewer">Viewer</option>
                <option value="editor">Editor</option>
              </select>
            </div>
            <button
              onClick={handleInvite}
              disabled={inviting || !inviteEmail.trim()}
              data-testid="send-invite-btn"
              className="px-4 py-2 text-xs font-heading uppercase tracking-wider bg-blue-600 hover:bg-blue-700 text-white rounded-sm transition-colors disabled:opacity-50"
            >
              {inviting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Invite'}
            </button>
          </div>
          {error && <p className="text-xs text-red-400" data-testid="invite-error">{error}</p>}
        </div>
      )}

      {/* Member List */}
      <div className="space-y-2">
        {members.map(m => {
          const cfg = ROLE_CONFIG[m.role] || ROLE_CONFIG.viewer;
          const Icon = cfg.icon;
          return (
            <div key={m.id} className="flex items-center justify-between bg-card border border-border rounded-sm px-4 py-3" data-testid={`member-row-${m.id}`}>
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${cfg.bg}`}>
                  <span className={`text-xs font-bold ${cfg.color}`}>
                    {(m.name || m.email || '?')[0].toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-foreground font-medium">{m.name || m.email}</p>
                  <p className="text-[11px] text-muted-foreground">{m.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {isOwner && !m.is_owner ? (
                  <select
                    value={m.role}
                    onChange={e => handleUpdateRole(m.id, e.target.value)}
                    data-testid={`role-select-${m.id}`}
                    className="bg-muted border border-border rounded-sm px-2 py-1 text-xs text-foreground/80 focus:outline-none focus:border-orange-800"
                  >
                    <option value="viewer">Viewer</option>
                    <option value="editor">Editor</option>
                  </select>
                ) : (
                  <RoleBadge role={m.role} />
                )}
                {isOwner && !m.is_owner && (
                  <button
                    onClick={() => handleRemove(m.id)}
                    data-testid={`remove-member-${m.id}`}
                    className="p-1.5 text-muted-foreground hover:text-red-400 transition-colors"
                    title="Remove member"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Role Legend */}
      <div className="bg-muted/30 border border-border/50 rounded-sm p-4">
        <p className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground mb-3">Role Permissions</p>
        <div className="grid grid-cols-3 gap-4 text-xs">
          <div>
            <div className="flex items-center gap-1.5 mb-1">
              <Crown className="w-3 h-3 text-amber-400" />
              <span className="text-amber-400 font-medium">Owner</span>
            </div>
            <p className="text-muted-foreground">Full access. Invite members, delete project, manage all settings.</p>
          </div>
          <div>
            <div className="flex items-center gap-1.5 mb-1">
              <Edit3 className="w-3 h-3 text-cyan-400" />
              <span className="text-cyan-400 font-medium">Editor</span>
            </div>
            <p className="text-muted-foreground">Upload documents, edit data, run AI tools. Cannot delete the project.</p>
          </div>
          <div>
            <div className="flex items-center gap-1.5 mb-1">
              <Eye className="w-3 h-3 text-muted-foreground" />
              <span className="text-muted-foreground font-medium">Viewer</span>
            </div>
            <p className="text-muted-foreground">Read-only access. View all project data, exports, and reports.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
